import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Form, Input, Select, TextArea, Typography } from '@/components';
import { DatacenterProjectTechnology } from '@/features/projects/types';
import { getDateFromQuarterAndYear, getQuarterFromDate, getYearFromDate, nullableStringSetter } from '@/helpers';
import { useDevelopmentStatus } from '@/hooks';
import { useDataCenterType } from '@/features/projects/hooks/use-datacenter-type-options';
import { useRedundancy } from '@/features/projects/hooks/use-redundancy-options';
import { useCoolingTechnology } from '@/features/projects/hooks/use-cooling-technology-options';
import { NUMERIC } from '@/features/projects/helpers/numeric-inputs';

export type DatacenterFormFields = {
  installed_capacity: number | null;
  type_project: string | null;
  development_status: string | null;
  ready_to_build: any;
  ready_to_build_quarter: string | null;
  ready_to_build_year: number | null;
  /* commercial_operation_date: any;
  commercial_operation_date_quarter: string | null;
  commercial_operation_date_year: number | null; */
  it_capacity: number | null;
  pue: string | null;
  data_center_type: string | null;
  redundancy: string | null;
  cooling_technology: string | null;
  comments: string | null;
};

export type DatacenterSubmitFields = {
  installed_capacity: number | null;
  type_project: string | null;
  development_status: string | null;
  ready_to_build: string | null;
  //commercial_operation_date: string | null;
  it_capacity: number | null;
  pue: string | null;
  data_center_type: string | null;
  redundancy: string | null;
  cooling_technology: string | null;
  comments: string | null;
};

type DatacenterFormProps = {
  readonly id: string;
  readonly item: DatacenterProjectTechnology;
  readonly onSubmit: (data: DatacenterSubmitFields) => void;
};

export function DatacenterForm({ id, item, onSubmit }: DatacenterFormProps) {
  const { t } = useTranslation(['projects']);
  const { options: developmentStatusOptions } = useDevelopmentStatus();
  const { options: dataCenterTypeOptions } = useDataCenterType();
  const { options: redundancyOptions } = useRedundancy();
  const { options: coolingTechnologyOptions } = useCoolingTechnology();

  const quarterOptions = [
    { label: 'Q1', value: '1' },
    { label: 'Q2', value: '2' },
    { label: 'Q3', value: '3' },
    { label: 'Q4', value: '4' },
  ];

  const options = useMemo(
    () => ({
      defaultValues: {
        installed_capacity: item.installed_capacity ?? null,
        type_project: item?.type_project ?? null,
        development_status: item.development_status ?? null,
        ready_to_build: item.ready_to_build ?? null,
        ready_to_build_quarter: getQuarterFromDate(item.ready_to_build),
        ready_to_build_year: getYearFromDate(item.ready_to_build),
        /* commercial_operation_date_quarter: getQuarterFromDate(item.commercial_operation_date),
        commercial_operation_date_year: getYearFromDate(item.commercial_operation_date),
        commercial_operation_date: item.commercial_operation_date ?? null, */
        it_capacity: item.it_capacity ?? null,
        pue: item.pue ?? null,
        data_center_type: item.data_center_type ?? null,
        redundancy: item.redundancy ?? null,
        cooling_technology: item.cooling_technology ?? null,
        comments: item.comments ?? null,
      },
    }),
    [item]
  );

  const onSubmitDatacenterForm = useCallback(
    (data: DatacenterFormFields) => {
      data.ready_to_build = getDateFromQuarterAndYear(data.ready_to_build_quarter, data.ready_to_build_year);
      /* data.commercial_operation_date = getDateFromQuarterAndYear(
        data.commercial_operation_date_quarter,
        data.commercial_operation_date_year
      ); */

      const payload = {
        installed_capacity: data.installed_capacity,
        type_project: data.type_project,
        development_status: data.development_status,
        ready_to_build: data.ready_to_build,
        //commercial_operation_date: data.commercial_operation_date,
        it_capacity: data.it_capacity,
        pue: data.pue,
        data_center_type: data.data_center_type,
        redundancy: data.redundancy,
        cooling_technology: data.cooling_technology,
        comments: data.comments,
      };

      onSubmit(payload);
    },
    [onSubmit]
  );

  return (
    <Form<DatacenterFormFields> id={id} onSubmit={(data) => onSubmitDatacenterForm(data)} options={options}>
      {({ formState, register }) => (
        <div className="space-y-6">
          <Typography as="p" color="secondary" size="body-3" weight="normal" className="mt-1 mb-10 italic">
            {t(
              'Please fill in this section with the main details about the project, including technology, development status, installed capacity, ready-to-build (RtB) and COD dates, etc.'
            )}
          </Typography>

          <Field
            id="installed_capacity"
            label={t('Installed Capacity')}
            error={formState.errors['installed_capacity']}
            delay={0.15}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="installed_capacity"
              type="number"
              suffix="MW"
              placeholder={t('Example: 379')}
              color={formState.errors['installed_capacity'] ? 'error' : 'primary'}
              autoComplete="off"
              registration={register('installed_capacity', { ...NUMERIC })}
            />
          </Field>

          <Field
            id="type_project"
            label={t('Type of Project')}
            error={formState.errors['type_project']}
            delay={0.15}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="type_project"
              type="text"
              color={formState.errors['type_project'] ? 'error' : 'primary'}
              registration={register('type_project', { setValueAs: nullableStringSetter })}
            />
          </Field>

          <Field
            id="development_status"
            label={t('Development status')}
            error={formState.errors['development_status']}
            delay={0}
          >
            <Select
              id="development_status"
              registration={register('development_status', { setValueAs: nullableStringSetter })}
              options={developmentStatusOptions}
              placeholder={t('Select a development status')}
              color={formState.errors['development_status'] ? 'error' : 'primary'}
            />
          </Field>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Field
              id="ready_to_build"
              label={t('Ready to Build Date')}
              error={formState.errors['ready_to_build_quarter'] || formState.errors['ready_to_build_year']}
              delay={0.25}
              classes={{ field: 'space-y-1' }}
            >
              <div className="flex gap-1">
                <Select
                  id="ready_to_build_quarter"
                  options={quarterOptions}
                  placeholder={t('Select a quarter')}
                  color={formState.errors['ready_to_build_quarter'] ? 'error' : 'primary'}
                  registration={register('ready_to_build_quarter')}
                />
                <Input
                  id="ready_to_build_year"
                  type="number"
                  step="1"
                  color={formState.errors['ready_to_build_year'] ? 'error' : 'primary'}
                  autoComplete="off"
                  registration={register('ready_to_build_year', { ...NUMERIC })}
                  placeholder={t('Year')}
                />
              </div>
            </Field>

            {/* <Field
              id="commercial_operation_date"
              label={t('Commercial Operation Date')}
              error={
                formState.errors['commercial_operation_date_quarter'] ||
                formState.errors['commercial_operation_date_year']
              }
              delay={0.25}
              classes={{ field: 'space-y-1' }}
            >
              <div className="flex gap-1">
                <Select
                id="commercial_operation_date_quarter"
                  options={quarterOptions}
                  placeholder={t('Select a quarter')}
                  color={formState.errors['commercial_operation_date_quarter'] ? 'error' : 'primary'}
                  registration={register('commercial_operation_date_quarter')}
                />
                <Input
                  id="commercial_operation_date_year"
                  type="number"
                  step="1"
                  color={formState.errors['commercial_operation_date_year'] ? 'error' : 'primary'}
                  autoComplete="off"
                  registration={register('commercial_operation_date_year', { ...NUMERIC })}
                  placeholder={t('Year')}
                  />
              </div>
            </Field> */}
          </div>

          <Field
            id="it_capacity"
            label={t('IT Capacity')}
            error={formState.errors['it_capacity']}
            delay={0.15}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="it_capacity"
              type="number"
              suffix="MW"
              placeholder={t('Example: 379')}
              color={formState.errors['it_capacity'] ? 'error' : 'primary'}
              autoComplete="off"
              registration={register('it_capacity', { ...NUMERIC })}
            />
          </Field>

          <Field
            id="pue"
            label={t('PUE')}
            error={formState.errors['pue']}
            delay={0.15}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="pue"
              type="text"
              color={formState.errors['pue'] ? 'error' : 'primary'}
              registration={register('pue', { setValueAs: nullableStringSetter })}
            />
          </Field>

          <Field
            id="data_center_type"
            label={t('Data Center Type')}
            error={formState.errors['data_center_type']}
            delay={0}
          >
            <Select
              id="data_center_type"
              registration={register('data_center_type', { setValueAs: nullableStringSetter })}
              options={dataCenterTypeOptions}
              placeholder={t('Select a Data Center type')}
              color={formState.errors['data_center_type'] ? 'error' : 'primary'}
            />
          </Field>

          <Field id="redundancy" label={t('Redundancy')} error={formState.errors['redundancy']} delay={0}>
            <Select
              id="redundancy"
              registration={register('redundancy', { setValueAs: nullableStringSetter })}
              options={redundancyOptions}
              placeholder={t('Select a redundancy option')}
              color={formState.errors['redundancy'] ? 'error' : 'primary'}
            />
          </Field>

          <Field
            id="cooling_technology"
            label={t('Cooling Technology')}
            error={formState.errors['cooling_technology']}
            delay={0}
          >
            <Select
              id="cooling_technology"
              registration={register('cooling_technology', { setValueAs: nullableStringSetter })}
              options={coolingTechnologyOptions}
              placeholder={t('Select a cooling technology option')}
              color={formState.errors['cooling_technology'] ? 'error' : 'primary'}
            />
          </Field>

          <Field
            id="comments"
            label={t('Comments')}
            error={formState.errors['comments']}
            delay={0.45}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea
              id="comments"
              color={formState.errors['comments'] ? 'error' : 'primary'}
              registration={register('comments', { setValueAs: nullableStringSetter })}
            />
          </Field>
        </div>
      )}
    </Form>
  );
}
